"use client";

import { Plus, TriangleAlert, X } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import {
  blendedMargin,
  maxDiscountForTargetMargin,
} from "@/lib/bf-toolkit/calculations";
import type { BundleItem } from "@/lib/bf-toolkit/calculations";
import { cn } from "@/lib/utils";

import { DEFAULTS, ITEM_LIMITS, RANGES, STRINGS } from "./strings";

interface BundlePricingProps {
  /** Request locale, used only for currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

type Mode = "max-discount" | "margin";

/** One editable row in the bundle. `name` is cosmetic; only the parsed price
 * and cost drive the maths. */
interface ItemRow {
  id: number;
  name: string;
  price: string;
  cost: string;
}

// Bump the number text well above the Input default so the figures a founder
// types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a money string the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can treat the row as incomplete
 * rather than pricing a bogus result.
 */
function parseMoney(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** Format a percentage to at most one decimal place, dropping a trailing ".0"
 * (46.0 → "46", 46.67 → "46.7"). */
function formatPct(value: number): string {
  return String(Number.parseFloat(value.toFixed(1)));
}

let nextRowId = 0;
function makeRow(seed?: {
  name: string;
  price: string;
  cost: string;
}): ItemRow {
  return {
    id: nextRowId++,
    name: seed?.name ?? "",
    price: seed?.price ?? "",
    cost: seed?.cost ?? "",
  };
}

export function BundlePricing({
  locale,
  currency,
  currencySymbol,
}: BundlePricingProps) {
  const [rows, setRows] = useState<ItemRow[]>(() =>
    DEFAULTS.items.map((item) => makeRow(item))
  );
  const [mode, setMode] = useState<Mode>("max-discount");
  const [targetMarginPct, setTargetMarginPct] = useState<number>(
    DEFAULTS.targetMarginPct
  );
  const [discountPct, setDiscountPct] = useState<number>(DEFAULTS.discountPct);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  // Only rows with a real price and a real (possibly zero) cost are priced; a
  // half-typed row is ignored rather than producing a bogus result.
  const items: BundleItem[] = useMemo(() => {
    const complete: BundleItem[] = [];
    for (const row of rows) {
      const price = parseMoney(row.price);
      const cost = parseMoney(row.cost);
      if (Number.isFinite(price) && price > 0 && Number.isFinite(cost)) {
        complete.push({ price, cost });
      }
    }
    return complete;
  }, [rows]);

  const state = useMemo(() => {
    // Natural (undiscounted) blend doubles as the "is there anything to price?"
    // check: NaN margin means no complete item rows yet.
    const natural = blendedMargin(items, 0);
    if (!Number.isFinite(natural.marginPct)) {
      return { kind: "empty" as const };
    }

    if (mode === "max-discount") {
      const maxDiscount = maxDiscountForTargetMargin(items, targetMarginPct);
      // Round the affordable discount DOWN so the displayed figure always still
      // clears the target — never up, which would quietly breach it.
      const maxDiscountFloor = Math.floor(maxDiscount);
      // No usable room: the bundle's natural margin is already at/below the
      // target, or there's less than a full point of discount to give.
      if (!(maxDiscount >= 1)) {
        return {
          kind: "no-room" as const,
          natural,
          belowTarget: maxDiscount <= 0,
        };
      }
      return {
        kind: "max-discount-ok" as const,
        natural,
        maxDiscount,
        maxDiscountFloor,
        atMax: blendedMargin(items, maxDiscountFloor),
      };
    }

    const blend = blendedMargin(items, discountPct);
    // Selling the bundle at or below cost — surface the loss rather than a quiet
    // negative percentage.
    if (blend.marginPct <= 0) {
      return { kind: "loss" as const, blend };
    }
    return { kind: "margin-ok" as const, blend };
  }, [items, mode, targetMarginPct, discountPct]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw money is intentionally not sent.
  const posthog = usePostHog();
  const itemCount = items.length;
  const analytics = useMemo((): Record<string, number> | null => {
    if (state.kind === "max-discount-ok") {
      return {
        targetMarginPct,
        maxDiscountPct: Math.round(state.maxDiscount * 10) / 10,
      };
    }
    if (state.kind === "margin-ok") {
      return {
        discountPct,
        marginPct: Math.round(state.blend.marginPct * 10) / 10,
      };
    }
    return null;
  }, [state, targetMarginPct, discountPct]);
  // Skip the very first run: the default inputs already produce a valid
  // result, and firing here would count a passive page load as a
  // "calculated" event rather than an actual user adjustment.
  const hasInteractedRef = useRef(false);
  useEffect(() => {
    if (!hasInteractedRef.current) {
      hasInteractedRef.current = true;
      return;
    }
    if (analytics === null) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "bundle-pricing",
        mode,
        itemCount,
        ...analytics,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, mode, itemCount, analytics]);

  function updateRow(id: number, patch: Partial<Omit<ItemRow, "id">>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }
  function addRow() {
    setRows((prev) =>
      prev.length >= ITEM_LIMITS.max ? prev : [...prev, makeRow()]
    );
  }
  function removeRow(id: number) {
    setRows((prev) =>
      prev.length <= ITEM_LIMITS.min ? prev : prev.filter((r) => r.id !== id)
    );
  }

  const isWarning = state.kind === "loss" || state.kind === "no-room";

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <ModeToggle mode={mode} onChange={setMode} />

      <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>

          <ul className="flex flex-col gap-4">
            {rows.map((row, index) => (
              <li key={row.id}>
                <ItemFields
                  row={row}
                  index={index}
                  symbol={currencySymbol}
                  canRemove={rows.length > ITEM_LIMITS.min}
                  onChange={(patch) => updateRow(row.id, patch)}
                  onRemove={() => removeRow(row.id)}
                />
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={addRow}
            disabled={rows.length >= ITEM_LIMITS.max}
            className="text-body-14 disabled:text-grey-400 text-pulse-700 inline-flex items-center gap-1.5 self-start font-semibold transition-colors hover:text-blue-800 disabled:cursor-not-allowed"
          >
            <Plus className="size-4" aria-hidden="true" />
            {STRINGS.addItem}
          </button>

          {mode === "max-discount" ? (
            <PercentField
              label={STRINGS.targetMargin}
              value={targetMarginPct}
              range={RANGES.targetMargin}
              onChange={setTargetMarginPct}
            />
          ) : (
            <PercentField
              label={STRINGS.discount}
              value={discountPct}
              range={RANGES.discount}
              onChange={setDiscountPct}
            />
          )}
        </div>

        {/* Output */}
        <div aria-live="polite" className={cnPanel(isWarning)}>
          {state.kind === "empty" && (
            <p className="text-body-16 text-text-label">
              Add at least one item with a price and cost to see your bundle
              margin.
            </p>
          )}

          {state.kind === "max-discount-ok" && (
            <div>
              <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
                At a {targetMarginPct}% target margin, you can discount this
                bundle
                <br />
                <span className="text-pulse-700">
                  up to {state.maxDiscountFloor}% off
                </span>{" "}
                and still hold it.
              </p>
              <dl className="border-grey-100 mt-6 flex flex-col gap-3 border-t pt-5">
                <SupportRow
                  label={STRINGS.listPrice}
                  value={formatCurrency(state.natural.totalPrice)}
                />
                <SupportRow
                  label={STRINGS.bundleCost}
                  value={formatCurrency(state.natural.totalCost)}
                />
                <SupportRow
                  label={STRINGS.marginBefore}
                  value={`${formatPct(state.natural.marginPct)}%`}
                />
                <SupportRow
                  label={STRINGS.discountedPrice}
                  value={formatCurrency(state.atMax.discountedRevenue)}
                />
              </dl>
            </div>
          )}

          {state.kind === "no-room" && (
            <div>
              <div className="flex items-start gap-3">
                <TriangleAlert
                  className="mt-1 size-6 shrink-0 text-red-600"
                  aria-hidden="true"
                />
                <p className="text-heading-24 lg:text-heading-32 text-red-700">
                  There&rsquo;s no room to discount this bundle.
                </p>
              </div>
              <p className="text-body-16 mt-3 text-red-700">
                {state.belowTarget
                  ? `Its blended margin is already ${formatPct(state.natural.marginPct)}%, at or below your ${targetMarginPct}% target. Lower the target or raise your prices to open up room to discount.`
                  : `Its blended margin of ${formatPct(state.natural.marginPct)}% is barely above your ${targetMarginPct}% target, so there's less than 1% to give away. Lower the target for more room.`}
              </p>
            </div>
          )}

          {state.kind === "margin-ok" && (
            <div>
              <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
                At {discountPct}% off, this bundle holds a
                <br />
                <span className="text-pulse-700">
                  {formatPct(state.blend.marginPct)}% blended margin
                </span>
                , {formatCurrency(state.blend.marginPerBundle)} per bundle.
              </p>
              <dl className="border-grey-100 mt-6 flex flex-col gap-3 border-t pt-5">
                <SupportRow
                  label={STRINGS.listPrice}
                  value={formatCurrency(state.blend.totalPrice)}
                />
                <SupportRow
                  label={STRINGS.discountedPrice}
                  value={formatCurrency(state.blend.discountedRevenue)}
                />
                <SupportRow
                  label={STRINGS.bundleCost}
                  value={formatCurrency(state.blend.totalCost)}
                />
                <SupportRow
                  label={STRINGS.marginPerBundle}
                  value={formatCurrency(state.blend.marginPerBundle)}
                />
              </dl>
            </div>
          )}

          {state.kind === "loss" && (
            <div>
              <div className="flex items-start gap-3">
                <TriangleAlert
                  className="mt-1 size-6 shrink-0 text-red-600"
                  aria-hidden="true"
                />
                <p className="text-heading-24 lg:text-heading-32 text-red-700">
                  {state.blend.marginPerBundle < 0 ? (
                    <>
                      You&rsquo;d lose{" "}
                      {formatCurrency(Math.abs(state.blend.marginPerBundle))} on
                      every bundle.
                    </>
                  ) : (
                    <>You&rsquo;d make no margin on this bundle.</>
                  )}
                </p>
              </div>
              <p className="text-body-16 mt-3 text-red-700">
                At {discountPct}% off you&rsquo;d sell it for{" "}
                {formatCurrency(state.blend.discountedRevenue)}, at or below its{" "}
                {formatCurrency(state.blend.totalCost)} cost. Ease off the
                discount to keep the bundle profitable.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-3xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-7 w-auto shrink-0 md:h-8 md:w-auto"
        />
      </div>
    </div>
  );
}

// Result panel background: blue for a healthy result, red for a loss or a
// bundle that's already too thin to discount.
function cnPanel(isWarning: boolean): string {
  const base =
    "flex min-h-[18rem] flex-col justify-center rounded-xl p-5 sm:p-6";
  return `${base} ${isWarning ? "bg-red-50" : "bg-pulse-50"}`;
}

function SupportRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-body-14 text-text-label">{label}</dt>
      <dd className="text-body-16 font-semibold text-[#0D0D0D]">{value}</dd>
    </div>
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
}) {
  const options: { value: Mode; label: string }[] = [
    { value: "max-discount", label: STRINGS.modeMaxDiscount },
    { value: "margin", label: STRINGS.modeMargin },
  ];
  return (
    <div>
      <span className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]">
        {STRINGS.modeLabel}
      </span>
      <div
        role="radiogroup"
        aria-label={STRINGS.modeLabel}
        className="border-grey-100 bg-gray-5 inline-flex rounded-full border p-1"
      >
        {options.map((option) => {
          const active = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                "text-body-14 focus-visible:ring-pulse-500 rounded-full px-4 py-2 font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-none",
                active
                  ? "bg-pulse-700 text-white"
                  : "text-grey-700 hover:text-black"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ItemFields({
  row,
  index,
  symbol,
  canRemove,
  onChange,
  onRemove,
}: {
  row: ItemRow;
  index: number;
  symbol: string;
  canRemove: boolean;
  onChange: (patch: Partial<Omit<ItemRow, "id">>) => void;
  onRemove: () => void;
}) {
  const nameId = useId();
  return (
    <div className="border-grey-100 rounded-xl border p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <label
          htmlFor={nameId}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {STRINGS.itemName}{" "}
          <span className="text-grey-400 font-normal">(optional)</span>
        </label>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`${STRINGS.removeItem} ${index + 1}`}
            className="text-grey-400 focus-visible:ring-pulse-500 -m-1 rounded-full p-1 transition-colors hover:text-black focus-visible:ring-2 focus-visible:outline-none"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <Input
        id={nameId}
        value={row.name}
        placeholder={`Item ${index + 1}`}
        onChange={(e) => onChange({ name: e.target.value })}
        className="text-body-16 mb-3 h-11"
      />
      <div className="grid grid-cols-2 gap-3">
        <MoneyField
          label={STRINGS.price}
          value={row.price}
          symbol={symbol}
          onChange={(price) => onChange({ price })}
        />
        <MoneyField
          label={STRINGS.cost}
          value={row.cost}
          symbol={symbol}
          onChange={(cost) => onChange({ cost })}
        />
      </div>
    </div>
  );
}

function MoneyField({
  label,
  value,
  symbol,
  onChange,
}: {
  label: string;
  value: string;
  symbol: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-14 pl-9 ${INPUT_TEXT}`}
        />
      </div>
    </div>
  );
}

function PercentField({
  label,
  value,
  range,
  onChange,
}: {
  label: string;
  value: number;
  range: { min: number; max: number; step: number };
  onChange: (v: number) => void;
}) {
  const id = useId();
  const { min, max, step } = range;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {label}
        </label>
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            aria-label={label}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => {
              const next = e.target.valueAsNumber;
              // Ignore NaN from an in-progress edit (e.g. a cleared field or a
              // trailing decimal point) instead of clamping mid-keystroke.
              if (Number.isNaN(next)) return;
              onChange(clamp(next, min, max));
            }}
            className={`h-14 w-24 [appearance:textfield] pr-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${INPUT_TEXT}`}
          />
          <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            %
          </span>
        </div>
      </div>
      <SliderRoot
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(vals) => onChange(clamp(vals[0] ?? min, min, max))}
        rangeClassName="bg-pulse-700"
        className="py-2"
      >
        <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
      </SliderRoot>
    </div>
  );
}
